import React, { useState } from 'react';
import { Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useAuth } from '../../contexts/AuthContext';
import { clearCameraUploads } from '../../api/cameraUpload';

interface ClearImagesButtonProps {
  deviceId: string;
  deviceName: string;
  disabled?: boolean;
  onCleared: () => void;
}

const ClearImagesButton: React.FC<ClearImagesButtonProps> = ({ deviceId, deviceName, disabled, onCleared }) => {
  const auth = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setClearing(true);
    setError('');
    try {
      await clearCameraUploads(deviceId, auth.tokens?.accessToken ?? '');
      setConfirmOpen(false);
      onCleared();
    } catch (err: any) {
      setError(err?.message || 'Failed to clear images');
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteForeverIcon />}
        onClick={() => setConfirmOpen(true)}
        disabled={disabled}
      >
        Clear images
      </Button>

      <Dialog open={confirmOpen} onClose={() => (clearing ? undefined : setConfirmOpen(false))}>
        <DialogTitle>Clear all images for "{deviceName}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes every uploaded photo and classification for this device, including any manual
            corrections. This can't be undone — you'll need to re-upload a zip afterward.
          </DialogContentText>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={clearing}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={clearing}>
            {clearing ? <CircularProgress size={20} /> : 'Delete everything'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClearImagesButton;
